import {GuideState} from '@mintdata-wsp/common/state/ui/visualDesigner/GuideState';
import _get from 'lodash/get';
import {CategoryResponse} from '@mintdata-wsp/common/controllers/network/extended/responses/CategoryResponse';
import {PropertyResponse} from '@mintdata-wsp/common/controllers/network/extended/responses/PropertyResponse';
import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {DesignRef} from '@mintdata-wsp/common/state/ui/publish/PublishState';
import {VisualArtifact} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualArtifact';
import {
    VisualComponent,
    VisualComponentId
} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualComponent';
import {CommandMenuState} from '@mintdata-wsp/common/state/ui/visualDesigner/CommandMenuState';
import {DesignState} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';
import {DesignVersionsState} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignVersionsState';
import {FlowState} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {GridMeasureRequest} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridMeasure';
import {CloudSheetSettings} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cloud/CloudSheetSettings';
import {CloudSlotsState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cloud/CloudSlotsState';
import {SheetSettingsId} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetSettingsTypes';
import {SurfaceEditorState} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {ToastData, ToastId} from '@mintdata-wsp/common/state/ui/visualDesigner/ToastData';
import {LearnExperienceState} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/LearnExperienceState';
import {WorkbenchState} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/WorkbenchState';
import {NameUtil} from '@mintdata-wsp/common/util/NameUtil';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {IObservableArray, observable} from 'mobx';
import {TopMenuState} from '@mintdata-wsp/common/state/ui/visualDesigner/TopMenuState';
import {ActiveUserEvent} from '@mintdata-wsp/wsp/controllers/multiplayer/MultiplayerController';

export enum CloudTickUnits
{
    NANOSECONDS = 'NANOSECONDS',
    MICROSECONDS = 'MICROSECONDS',
    MILLISECONDS = 'MILLISECONDS',
    SECONDS = 'SECONDS',
    MINUTES = 'MINUTES',
    HOURS = 'HOURS',
    DAYS = 'DAYS'
}

export class CloudTick
{
    constructor(public readonly name:string,
                public readonly description:string,
                public readonly interval:number,
                public readonly units:CloudTickUnits)
    {
    }

    public static fromJson(json:CloudTick):CloudTick
    {
        return new CloudTick(json.name, json.description, json.interval, json.units);
    }
}

export class CloudPort
{
    constructor(public name:string,
                public description:string,
                public isOptional:boolean)
    {
    }

    public static fromJson(json:CloudPort):CloudPort
    {
        return new CloudPort(json.name, json.description, json.isOptional);
    }
}

export enum PropertyWidgetName
{
    TextInput = 'TextInput',
    Checkbox = 'Checkbox',
    Dropdown = 'Dropdown',
    Suggest = 'Suggest',
    EditableSuggest = 'EditableSuggest',
    MultiSuggest = 'MultiSuggest',
    EditableMultiSuggest = 'EditableMultiSuggest',
    NumericInput = 'NumericInput',
    Textarea = 'Textarea',
    RadioButtonGroup = 'RadioButtonGroup',
    Password = 'Password',
    URL = 'URL',
    Hostname = 'Hostname',
    Port = 'Port',
    Email = 'Email',
    Date = 'Date',
    DateTime = 'DateTime',
    FrameExpression = 'FrameExpression',
    FrameColumnName = 'FrameColumnName',
    FrameColumnNames = 'FrameColumnNames',
    SpreadsheetRange = 'SpreadsheetRange',
    SQL = 'SQL',
    JSON = 'JSON',
    Javascript = 'Javascript',
    List = 'List',
    Map = 'Map',
    Multiline = 'Multiline',
}

export enum PropertyType
{
    String = 'String',
    Number = 'Number',
    Boolean = 'Boolean',
    List = 'List',
    Map = 'Map',
}

export type PropertyValidationExpr = string;
export type PropertyVisibilityExpr = string;

export interface IPropertyValidation
{
    rule:PropertyValidationExpr;
    message:string;
}

export interface IPropertyMapInfo
{
    key:CloudProperty;
    value:CloudProperty;
}

export class CloudEnumEntry
{
    constructor(public readonly caption:string, public readonly value:any)
    {
    }
}

export class CloudEnum
{
    constructor(public readonly entries:CloudEnumEntry[])
    {
    }
}

export class CloudProperty
{
    constructor(public name:string,
                public description:string,
                public label:string,
                public type:PropertyType,
                public placeholder:string,
                public defaultValue?:any,
                public widget?:PropertyWidgetName,
                public category?:string,
                public index?:number,
                public enumValues?:CloudEnum,
                public isRequired?:boolean,
                public validation?:IPropertyValidation[],
                public visibility?:PropertyVisibilityExpr,
                public mapInfo?:IPropertyMapInfo,
                public listItem?:CloudProperty)
    {
    }

    public static fromJson(json:CloudProperty):CloudProperty
    {
        const mapInfo:IPropertyMapInfo = json.mapInfo && {
            key: CloudProperty.fromJson(json.mapInfo.key),
            value: CloudProperty.fromJson(json.mapInfo.value)
        };

        const listItem:CloudProperty = json.listItem && CloudProperty.fromJson(json.listItem);

        return new CloudProperty(
            json.name,
            json.description,
            json.label,
            json.type,
            json.placeholder,
            json.defaultValue,
            json.widget,
            json.category,
            json.index,
            json.enumValues,
            json.isRequired,
            json.validation,
            json.visibility,
            mapInfo,
            listItem
        );
    }

    public static fromResponse(prop:PropertyResponse):CloudProperty
    {
        const label = NameUtil.getPropertyDisplayName(prop);
        const defaultValue = CloudProperty._getDefaultValue(prop);
        const type = CloudProperty._getType(prop);
        const widget = CloudProperty._getWidget(prop);
        const enumValues = CloudProperty._getEnumValues(prop);
        const mapInfo = CloudProperty._getMapInfo(prop);
        const listItem = CloudProperty._getListItem(prop);

        return new CloudProperty(
            prop.name,
            prop.description,
            label,
            type,
            prop.placeholder,
            defaultValue,
            widget,
            prop.category,
            prop.index,
            enumValues,
            !prop.nullable,
            prop.validation,
            prop.visibility,
            mapInfo,
            listItem
        );
    }

    public get isEnum():boolean
    {
        return !!this.enumValues;
    }

    public get isJavascript():boolean
    {
        return this.widget === PropertyWidgetName.Javascript;
    }

    public withName(name:string):CloudProperty
    {
        return new CloudProperty(
            name,
            this.description,
            this.label,
            this.type,
            this.placeholder,
            this.defaultValue,
            this.widget,
            this.category,
            this.index,
            this.enumValues,
            this.isRequired,
            this.validation,
            this.visibility,
            this.mapInfo,
            this.listItem
        );
    }

    private static _getType(prop:PropertyResponse):PropertyType
    {
        const cloudTypesManager = commonControllers.artifacts.getCloudTypesManager();
        const {typeDescriptor, enumValues} = prop;

        if( enumValues )
        {
            return PropertyType.String;
        }

        return cloudTypesManager.getType(typeDescriptor);
    }

    private static _getDefaultValue(prop:PropertyResponse):any
    {
        let {defaultValue} = prop;
        const enumValues = CloudProperty._getEnumValues(prop);

        if( defaultValue == null && enumValues )
        {
            defaultValue = enumValues.entries[0].value;
        }

        return defaultValue;
    }

    private static _getWidget(prop:PropertyResponse):PropertyWidgetName
    {
        return CloudProperty._responseWidgetMap[prop.widget];
    }

    private static _getEnumValues(prop:PropertyResponse):CloudEnum
    {
        const {enumValues, enumLabels = []} = prop;

        if( enumValues && enumValues.length > 0 )
        {
            const entries = enumValues.map((item) =>
            {
                const key = _get(item, 'key');

                const labelObj = enumLabels.find(item => _get(item, 'key') === key);
                const label = labelObj ? _get(labelObj, 'label') : _get(item, 'label');

                return new CloudEnumEntry(label, key);
            });

            return new CloudEnum(entries);
        }
    }

    private static _getMapInfo(prop:PropertyResponse):IPropertyMapInfo
    {
        if( CloudProperty._getType(prop) === PropertyType.Map )
        {
            const cloudTypesManager = commonControllers.artifacts.getCloudTypesManager();
            const {typeDescriptor} = prop;
            const keyProp = _get(prop, 'mapInfo.key', {});
            const valueProp = _get(prop, 'mapInfo.value', {});

            const [keyTd, valueTd] = cloudTypesManager.getMapKeyValueTypes(typeDescriptor);

            keyProp.typeDescriptor = keyTd;
            keyProp.name = 'key';

            valueProp.typeDescriptor = valueTd;
            valueProp.name = 'value';

            return {
                key: CloudProperty.fromResponse(keyProp),
                value: CloudProperty.fromResponse(valueProp)
            };
        }
    }

    private static _getListItem(prop:PropertyResponse):CloudProperty
    {
        if( CloudProperty._getType(prop) === PropertyType.List )
        {
            const cloudTypesManager = commonControllers.artifacts.getCloudTypesManager();
            const {typeDescriptor} = prop;
            const itemProp:any = _get(prop, 'listItem', {});

            itemProp.typeDescriptor = cloudTypesManager.getListItemType(typeDescriptor);
            itemProp.name = 'value';

            return CloudProperty.fromResponse(itemProp);
        }
    }

    private static _responseWidgetMap = {
        textInput: PropertyWidgetName.TextInput,
        checkbox: PropertyWidgetName.Checkbox,
        dropdown: PropertyWidgetName.Dropdown,
        suggest: PropertyWidgetName.Suggest,
        editableSuggest: PropertyWidgetName.EditableSuggest,
        multiSuggest: PropertyWidgetName.MultiSuggest,
        editableMultiSuggest: PropertyWidgetName.EditableMultiSuggest,
        numericInput: PropertyWidgetName.NumericInput,
        textarea: PropertyWidgetName.Textarea,
        radioButtonGroup: PropertyWidgetName.RadioButtonGroup,
        password: PropertyWidgetName.Password,
        url: PropertyWidgetName.URL,
        hostname: PropertyWidgetName.Hostname,
        port: PropertyWidgetName.Port,
        email: PropertyWidgetName.Email,
        date: PropertyWidgetName.Date,
        dateTime: PropertyWidgetName.DateTime,
        frameExpression: PropertyWidgetName.FrameExpression,
        frameColumnName: PropertyWidgetName.FrameColumnName,
        frameColumnNames: PropertyWidgetName.FrameColumnNames,
        spreadsheetRange: PropertyWidgetName.SpreadsheetRange,
        sql: PropertyWidgetName.SQL,
        json: PropertyWidgetName.JSON,
        javascript: PropertyWidgetName.Javascript,
        multiline: PropertyWidgetName.Multiline
    };
}

export class CloudCategory
{
    constructor(public readonly order:number,
                public readonly key:string,
                public readonly label:string)
    {
    }

    public static fromJson(json:CloudCategory):CloudCategory
    {
        return new CloudCategory(json.order, json.key, json.label);
    }

    public static fromResponse(category:CategoryResponse):CloudCategory
    {
        return new CloudCategory(category.order, category.key, category.label);
    }
}

export class CloudComponentGroup
{
    constructor(public readonly id:string,
                public readonly artifactName:string,
                public readonly artifactFriendlyName:string,
                public readonly sectionName:string)
    {
    }

    public static fromJson(json:CloudComponentGroup):CloudComponentGroup
    {
        return new CloudComponentGroup(json.id, json.artifactName, json.artifactFriendlyName, json.sectionName);
    }
}

export type CloudComponentId = GUID;

export class CloudComponent
{
    public readonly id:CloudComponentId = IdUtil.newGUID();

    constructor(public readonly group:CloudComponentGroup,
                public readonly javaClassFQN:string,
                public readonly name:string,
                public readonly description:any,
                public readonly properties:CloudProperty[],
                public readonly categories:CloudCategory[],
                public readonly ticks:CloudTick[],
                public readonly inputPorts:CloudPort[],
                public readonly outputPorts:CloudPort[])
    {
    }

    public static fromJson(json:CloudComponent):CloudComponent
    {
        const group = CloudComponentGroup.fromJson(json.group);

        const properties = json.properties.map((p:CloudProperty) =>
        {
            return CloudProperty.fromJson(p);
        });

        const categories = json.categories.map((c:CloudCategory) =>
        {
            return CloudCategory.fromJson(c);
        });

        const ticks = json.ticks.map((t:CloudTick) =>
        {
            return CloudTick.fromJson(t);
        });

        const inPorts = json.inputPorts.map((p:CloudPort) =>
        {
            return CloudPort.fromJson(p);
        });

        const outPorts = json.outputPorts.map((p:CloudPort) =>
        {
            return CloudPort.fromJson(p);
        });

        return new CloudComponent(
            group,
            json.javaClassFQN,
            json.name,
            json.description,
            properties,
            categories,
            ticks,
            inPorts,
            outPorts
        );
    }
}

export class ResolvedCloudArtifact
{
    constructor(public readonly friendlyName:string,
                public readonly groupId:string,
                public readonly artifactId:string,
                public readonly version:string,
                public readonly components:CloudComponent[])
    {
    }

    public get id():string
    {
        return this.getMavenDescriptor();
    }

    getMavenDescriptor():string
    {
        return `${this.groupId}:${this.artifactId}:${this.version}`;
    }
}

export class ArtifactsState
{
    @observable
    public cloudArtifacts:ResolvedCloudArtifact[] = null;

    @observable
    public visualArtifacts:VisualArtifact[] = null;

    @observable
    public visualComponents:Map<VisualComponentId, VisualComponent> = new Map<VisualComponentId, VisualComponent>();

    public visualArtifactsLoadingPromise:Promise<boolean> = null;
}

export class VisualDesignerMenuState
{
    @observable
    public isMainMenuOpen:boolean = false;

    @observable
    public openMenuItemId:string = null;
}

export class OpenDesignState
{
    @observable
    public isPublishMode:boolean = false;

    @observable
    public initialized:boolean = false;

    @observable
    public locationHash:string = null;

    @observable
    public designState:DesignState = null;

    @observable
    public cloudArtifacts:ResolvedCloudArtifact[] = null;

    @observable
    public cloudSlotsStateMap:Map<SheetId, CloudSlotsState> = new Map();

    @observable
    public cloudSheetSettingsMap:Map<SheetSettingsId, CloudSheetSettings> = new Map();

    constructor(public readonly designRef:DesignRef,
                public readonly isMultiplayerMode:boolean = false,
                isPublishMode:boolean = false)
    {
        this.isPublishMode = isPublishMode;
    }
}

export class GridMeasureState
{
    @observable
    public measureRequests:GridMeasureRequest[] = [];
}

export class ToastsState
{
    @observable
    public toasts:Map<ToastId, ToastData> = new Map<ToastId, ToastData>();
}

export enum SampleDesignFocusTarget
{
    Surface = 'Surface',
    Spreadsheet = 'Spreadsheet',
}

export enum SampleDesignCellBehavior
{
    openFormulaEditor = 'openFormulaEditor',
    openNestedSpreadsheet = 'openNestedSpreadsheet',
}

export interface ISampleDesignState
{
    showSurface:boolean;
    showPropPanel:boolean;
    showOutlinePanel:boolean;
    selectedPage:string;
    selectedComponentName:string;

    showSpreadsheet:boolean;
    showStyleBar:boolean;
    spreadsheetWidthPercent:number;
    selectedSheet:string;
    selectedSheetRef:string;

    showFlowEditor:boolean;
    showFlowPropPanel:boolean;
    selectedFlowBlock:string;
    canSwitchToFlow:boolean; // additional flag

    cellBehavior:SampleDesignCellBehavior;
    formulaCursorOffset:number;
    showFormulaTypeahead:boolean;
    showFormulaDocs:boolean;
    selectedNestedPath:string;
}

export class SampleDesignState implements ISampleDesignState
{
    @observable public embedded:boolean = false;

    @observable public showSurface:boolean = false;
    @observable public showPropPanel:boolean = false;
    @observable public showOutlinePanel:boolean = false;
    @observable public selectedPage:string = null; // Page name (Defaults to Starting Page)
    @observable public selectedComponentName:string = null; // Box name on surface

    @observable public showSpreadsheet:boolean = false;
    @observable public showStyleBar:boolean = false;
    @observable public spreadsheetWidthPercent:number = null; // number between 0 and 100
    @observable public selectedSheet:string = null; // Sheet name (Defaults to Sheet 1)
    @observable public selectedSheetRef:string = null; // cell or range

    @observable public showFlowEditor:boolean = false;
    @observable public showFlowPropPanel:boolean = false;
    @observable public selectedFlowBlock:string = null;
    @observable public canSwitchToFlow:boolean = false; // additional flag for switching to/from surface/spreadSheet

    // Cell Behavior
    // openFormulaEditor | openNestedSpreadsheet | none
    // default = none
    @observable public cellBehavior:SampleDesignCellBehavior = null;

    // cellBehavior == openFormulaEditor
    @observable public formulaCursorOffset:number = null;
    @observable public showFormulaTypeahead:boolean = false;
    @observable public showFormulaDocs:boolean = false;

    // cellBehavior == openNestedSpreadsheet
    // /results/1/geometry/1/lat/1
    // 1) always highlight last pathPart
    // 2) presume /1 if not present at end.
    @observable public selectedNestedPath:string = null;

    public static defaultParams():SampleDesignState
    {
        return new SampleDesignState();
    }
}

export interface IActiveUserDetails
{
    fullName:string;
    avatarUrl:string;
    color:string;
    isIdle:boolean;
    idleHandlerId:any;
    lastUpdated:number;
}

export interface IBoxMoveState
{
    userId:number;
    isReparenting:boolean;
    left:number;
    top:number;
    right:number;
    bottom:number;
    width:number;
    height:number;

    // internal
    isDone?:boolean;
}
export class ActiveUsersState
{
    @observable public thisUser:ActiveUserEvent = { userId: 0, timestamp: 0};
    @observable public otherUsers:IObservableArray<ActiveUserEvent> = observable.array<ActiveUserEvent>();

    @observable public otherUsersDetails:Map<number, IActiveUserDetails> = new Map();
    @observable public followingUserId:number = 0;

    @observable public otherUserSelectionState:Map<string, {userId}[]> = new Map();

    @observable public otherUserBoxMoveState:Map<string, IBoxMoveState[]> = new Map();
    
    @observable public otherUserBoxReparentingState:Map<string, IBoxMoveState[]> = new Map();
}


export class VisualDesignerState
{
    public readonly artifacts:ArtifactsState = new ArtifactsState();
    public readonly menu:VisualDesignerMenuState = new VisualDesignerMenuState();

    @observable
    public openDesign:OpenDesignState = null;

    @observable
    public sampleDesign:SampleDesignState = null;

    public readonly workbench:WorkbenchState = new WorkbenchState();
    public readonly flow:FlowState = new FlowState();
    public readonly surfaceEditor:SurfaceEditorState = new SurfaceEditorState();

    public readonly lx:LearnExperienceState = new LearnExperienceState();

    // Used for off-screen measure of debug data table UI
    public readonly gridMeasure:GridMeasureState = new GridMeasureState();
    public readonly toastsState:ToastsState = new ToastsState();

    public readonly commandMenuState:CommandMenuState = new CommandMenuState();
    public readonly topMenuState:TopMenuState = new TopMenuState();

    public readonly designVersions:DesignVersionsState = new DesignVersionsState();

    public readonly guideState:GuideState = new GuideState();

    public activeUsers:ActiveUsersState = new ActiveUsersState();
}
